import {
  useCallback, useMemo, useState, useEffect,
} from 'react'
import {
  PGA_COACH_DIRECTORY_SAVED_COACHES_KEY,
  getSavedCoachIds,
  toggleSavedCoach,
} from '@lib/coaches-directory/saved-coaches'

/**
 * Tracks coach object IDs persisted in localStorage for the `/coaches/search`
 * directory. Cross-tab updates flow through the storage event so the filter
 * toggle and every visible heart stay in sync without a backend.
 *
 * Mirrors `usePgaCoachSavedOfferings` so future consolidation onto a single
 * "saved" abstraction is straightforward.
 */
export default function useSavedCoaches() {
  const [savedCoachIds, setSavedCoachIds] = useState([])

  const savedIdSet = useMemo(
    () => new Set(savedCoachIds),
    [savedCoachIds],
  )

  const refresh = useCallback(() => {
    setSavedCoachIds(getSavedCoachIds())
  }, [])

  const toggle = useCallback((coachId) => {
    const nextSaved = toggleSavedCoach(coachId)
    refresh()
    return nextSaved
  }, [refresh])

  useEffect(() => {
    refresh()
    const handleStorage = (event) => {
      if (event.key === PGA_COACH_DIRECTORY_SAVED_COACHES_KEY) refresh()
    }
    window.addEventListener('storage', handleStorage)
    return () => window.removeEventListener('storage', handleStorage)
  }, [refresh])

  const isCoachSaved = useCallback(
    (coachId) => (coachId != null ? savedIdSet.has(String(coachId)) : false),
    [savedIdSet],
  )

  return {
    savedIdSet, toggle, refresh, isCoachSaved,
  }
}
